import './styles.scss';
import {useLexicalComposerContext} from "@lexical/react/LexicalComposerContext";
import {
    LexicalTypeaheadMenuPlugin,
    TypeaheadOption,
    useBasicTypeaheadTriggerMatch
} from "@lexical/react/LexicalTypeaheadMenuPlugin";
import {useCallback, useEffect, useMemo, useState} from "react";
import * as React from "react";
import * as ReactDOM from "react-dom";
import {$createMentionNode} from "./MentionNode";

const SUGGESTION_LIST_LENGTH_LIMIT = 5;
const mentionsCache = new Map();
const searchMention  = (string,mentions = [], callback)=> {
    const results = mentions.filter(({username = ""}) => username.toLowerCase().includes(string.toLowerCase()));
    callback(results);
}

function useMentionLookupService(mentionString, mentions =[]) {
    const [results, setResults] = useState([]);
    useEffect(() => {
        const cachedResults = mentionsCache.get(mentionString);
        if (mentionString == null) {
            setResults([]);
            return;
        }
        if (cachedResults === null) {
            return;
        } else if (cachedResults !== undefined) {
            setResults(cachedResults);
            return;
        }
        mentionsCache.set(mentionString, null);
        searchMention(mentionString, mentions, (newResults) => {
            mentionsCache.set(mentionString, newResults);
            setResults(newResults);
        });
    }, [mentionString, mentions]);
    return results;
}

class MentionTypeaheadOption extends TypeaheadOption {
    constructor(name, userId, picture) {
        super(name);
        this.name = name;
        this.userId = userId;
        this.picture = picture;
    }
}

function MentionsTypeaheadMenuItem({index, isSelected, onClick, onMouseEnter, option}) {
    let className = "item";
    if (isSelected) {
        className += " selected";
    }
    return (<li key={option.key} tabIndex={-1} className={className} ref={option.setRefElement} role="option"
                aria-selected={isSelected} id={"typeahead-item-" + index} onMouseEnter={onMouseEnter} onClick={onClick}>
        {option.picture}
        <span className="text">{option.name}</span>
    </li>);
}

export default function MentionsPlugin({mentions = []}) {
    const [editor] = useLexicalComposerContext();
    const [queryString, setQueryString] = useState(null);
    const results = useMentionLookupService(queryString, mentions);

    const checkForSlashTriggerMatch = useBasicTypeaheadTriggerMatch("@", {
        minLength: 0
    });
    const options = useMemo(() => results
        .map((mention) => new MentionTypeaheadOption(mention.username, mention._id, <i/>))
        .slice(0, SUGGESTION_LIST_LENGTH_LIMIT), [results]);

    const onSelectOption = useCallback((selectedOption, nodeToReplace, closeMenu) => {
        editor.update(() => {
            const mentionNode = $createMentionNode(selectedOption.name, selectedOption.userId);
            if (nodeToReplace) {
                nodeToReplace.replace(mentionNode);
            }
            mentionNode.select();
            closeMenu();
        });
    }, [editor]);
    const checkForMentionMatch = useCallback((text) => {
        const slashMatch = checkForSlashTriggerMatch(text, editor);
        return slashMatch || null;
    }, [checkForSlashTriggerMatch, editor]);
    return (
        <LexicalTypeaheadMenuPlugin
            onQueryChange={setQueryString}
            onSelectOption={onSelectOption}
            triggerFn={checkForMentionMatch}
            options={options}
            menuRenderFn={(anchorElementRef, {
                selectedIndex,
                selectOptionAndCleanUp,
                setHighlightedIndex
            }) => anchorElementRef.current && results.length
                ? ReactDOM.createPortal(<div className="typeahead-popover mentions-menu">
                    <ul>
                        {options.map((option, i) => (
                            <MentionsTypeaheadMenuItem index={i}
                                isSelected={selectedIndex === i}
                                onClick={() => {
                                    setHighlightedIndex(i);
                                    selectOptionAndCleanUp(option);
                                }}
                                onMouseEnter={() => {
                                    setHighlightedIndex(i);
                                }}
                                key={option.key}
                                option={option}/>)
                        )}
                    </ul>
                </div>, anchorElementRef.current)
                : null}
        />
    );
}
